import { usePersistedState } from "@/lib/persistedFilters";
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { usePermissionPreview } from "@/components/permissions/PermissionPreview";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Plus, MoreHorizontal, Pencil, Trash2, Search, Handshake, UserCheck, UserX, Palette, Users as UsersIcon, Loader2, ShieldCheck, AppWindow, VenetianMask, Image as ImageIcon, BarChart3, Download, FileText, X as XIcon, Mail, MessageSquare, Columns3, ChevronUp, ChevronDown, GripVertical, DollarSign, Phone, Globe, Hash, GitMerge, LifeBuoy, ArrowLeft, ExternalLink, Tag, LogOut, KeyRound } from "lucide-react";
import { SignOutEverywhereDialog, toastAccessReset } from "@/components/SignOutEverywhere";
import { ResetMfaDialog, canResetMfa } from "@/components/ResetMfaDialog";
import api, { formatApiErrorDetail } from "@/lib/api";
import { downloadFromUrl } from "@/lib/download";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { PartnerBrandingForm, brandingFormOf } from "@/components/PartnerBrandingForm";
import { StatusChip } from "@/components/Badges";
import { useHelpdeskLive } from "@/hooks/useHelpdeskLive";
import { PartnerReadinessWidget } from "@/components/PartnerReadinessWidget";
import { CollapsibleWidgetShell } from "@/components/CollapsibleWidgetShell";
import { ColumnsToolbar } from "@/components/ColumnsToolbar";
import { SearchMatchHint } from "@/components/SearchMatchHint";
import { useUserSort, SortHeader, NameSortHint, LastNameSwitch } from "@/components/UserSortControls";
import { UsersCsvButton } from "@/components/UsersCsvButton";
import { useOrgSort } from "@/components/OrgSortControls";

const PARTNER_SORTABLE = ["partner_success", "users", "status"];
const PARTNER_USER_CSV = ["name", "email", "phone", "organization", "mfa", "last_login", "welcome", "role", "status"];
import { EffectivePermsDialog } from "@/components/EffectivePerms";
import { AppAssignDialog } from "@/components/AppAssign";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

// Iter 115s — BACKLOG: Helpdesk + QBR columns are hidden for every user until
// those apps are ready. Set to true to bring them back (the permission gates
// below still apply once enabled).
const HELPDESK_QBR_COLUMNS_ENABLED = false;

const EMPTY = { name: "", type: "partner", partner_type: "none", status: "active", contact_name: "", contact_email: "", contact_phone: "", notes: "", commission_alias: "", tax_rate: "", tax_jurisdiction: "", tax_number: "", territory: "" };
const EMPTY_USER = { name: "", email: "", password: "", role: "partner", territory: "", quoter_username: "" };

// Canadian sales-tax presets — picking a jurisdiction auto-sets the rate.
// `label` shows in the dropdown, `value` is what gets saved on the org.
// "Custom / Other" keeps the fields freeform for non-CA partners.
export const CA_TAX_PRESETS = [
  { code: "AB",  label: "Alberta · GST 5%",              rate: 5.0,    value: "AB · GST" },
  { code: "BC",  label: "British Columbia · GST+PST 12%", rate: 12.0,   value: "BC · GST + PST" },
  { code: "MB",  label: "Manitoba · GST+PST 12%",        rate: 12.0,   value: "MB · GST + PST" },
  { code: "NB",  label: "New Brunswick · HST 15%",       rate: 15.0,   value: "NB · HST" },
  { code: "NL",  label: "Newfoundland and Labrador · HST 15%", rate: 15.0, value: "NL · HST" },
  { code: "NS",  label: "Nova Scotia · HST 14%",         rate: 14.0,   value: "NS · HST" },
  { code: "NT",  label: "Northwest Territories · GST 5%", rate: 5.0,   value: "NT · GST" },
  { code: "NU",  label: "Nunavut · GST 5%",              rate: 5.0,    value: "NU · GST" },
  { code: "ON",  label: "Ontario · HST 13%",             rate: 13.0,   value: "ON · HST" },
  { code: "PE",  label: "Prince Edward Island · HST 15%", rate: 15.0,  value: "PE · HST" },
  { code: "QC",  label: "Quebec · GST+QST 14.975%",      rate: 14.975, value: "QC · GST + QST" },
  { code: "SK",  label: "Saskatchewan · GST+PST 11%",    rate: 11.0,   value: "SK · GST + PST" },
  { code: "YT",  label: "Yukon · GST 5%",                rate: 5.0,    value: "YT · GST" },
];

export default function Partners() {
  const { has, user } = useAuth();
  const navigate = useNavigate();
  const helpdeskLive = useHelpdeskLive();
  const [searchParams, setSearchParams] = useSearchParams();
  // Iter 111 — QBR column hidden from partner-role viewers (moved to
  // Phase 2 Backlog). Staff still see it when they hold `qbr.view`.
  // Do NOT remind about re-enabling until explicitly asked.
  // Iter 114 — QBR is opt-in: the button shows only for users explicitly
  // granted `qbr.view` (staff or partner). No role holds it by default.
  const canViewQbr = has("qbr.view");
  // Iter 114d — Helpdesk column is gated on `helpdesk.view`, same as the QBR one.
  // Iter 115i — Helpdesk data is super-admin only.
  const canViewHelpdesk = has("helpdesk.view")
    && (user?.role === "super_admin" || user?.base_role === "super_admin");
  // Iter 111 — Partner-role viewers see this page as their team roster,
  // not the Conduit partner directory. We show them the Users list for
  // their own org (they can only ever manage a single partner — their
  // own), rebrand the page as "Staff", and hide the multi-partner
  // table + widgets + CSV/PDF actions.
  const isPartnerViewer = (user?.base_role || user?.role) === "partner";
  // Iter 272 — a Partner Success manager can't reassign the partners they own,
  // but `partners_all.view` holders see (and pick up) every partner, including
  // unassigned ones, so they keep the Partner Success filter + assignment.
  const canManagePS = has("organizations.edit")
    && (user?.role !== "partner_success" || has("partners_all.view"));
  const [orgs, setOrgs] = useState([]);
  const [partnerTypes, setPartnerTypes] = useState([]);
  const [territories, setTerritories] = useState([]);
  const [roles, setRoles] = useState([]);
  const [psStaff, setPsStaff] = useState([]);
  const [psBulkOpen, setPsBulkOpen] = useState(false);
  const [psBulkValue, setPsBulkValue] = useState("none");
  const [psBulkBusy, setPsBulkBusy] = useState(false);
  // Iter 306 — bulk Partner Success assignment applies only to ticked rows.
  // Selection survives filter/search changes so a manager can build it up
  // across several filters; the "N selected" chip keeps that visible.
  const [selected, setSelected] = useState(() => new Map());
  const toggleSelected = (o) => setSelected((prev) => {
    const next = new Map(prev);
    if (next.has(o.org_id)) next.delete(o.org_id); else next.set(o.org_id, o.name);
    return next;
  });
  const [search, setSearch] = usePersistedState("partners.search", "", searchParams.get("q"));
  // Iter 110ai — Split UI state (`searchDraft`, updates instantly on
  // every keystroke) from the state that triggers renders + refetches
  // (`search`). Debouncing the propagation keeps the massive Partners
  // table + widgets from re-rendering on every keypress.
  const [searchDraft, setSearchDraft] = useState(search);
  const pushedSearch = useRef(search);
  useEffect(() => {
    // "Clear filters" (or a deep link) changed the search from outside the box — mirror it.
    if (search !== pushedSearch.current) { pushedSearch.current = search; setSearchDraft(search); }
  }, [search]);
  useEffect(() => {
    if (searchDraft === search) return;
    const id = setTimeout(() => { pushedSearch.current = searchDraft; setSearch(searchDraft); }, 200);
    return () => clearTimeout(id);
  }, [searchDraft, search, setSearch]);
  const [filterPartnerType, setFilterPartnerType] = usePersistedState("partners.type", "all");
  const [filterStatus, setFilterStatus] = usePersistedState("partners.status", "all");
  const [filterPS, setFilterPS] = usePersistedState("partners.psm", "all");
  // Client-side restriction (e.g. Commissions → Partners is restricted to
  // Agent + Master Agent by default). Populated from ?types=Agent,Master Agent.
  const restrictedTypes = useMemo(() => {
    const raw = searchParams.get("types");
    if (!raw) return null;
    const list = raw.split(",").map((s) => s.trim()).filter(Boolean);
    return list.length ? list : null;
  }, [searchParams]);
  const clearRestriction = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("types");
    setSearchParams(next, { replace: true });
  };
  const [exporting, setExporting] = useState(false);
  const [openCounts, setOpenCounts] = useState({});
  const [didCounts, setDidCounts] = useState({});
  const [tablePrefs, setTablePrefs] = useState({ visible: null });
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [terrCustom, setTerrCustom] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [deleteTarget, setDeleteTarget] = useState(null);
  // Iter 87: Partner Merge — fold source→target (moves DIDs, merges
  // aliases, adds source name as alias, soft-marks source).
  const [mergeSource,   setMergeSource]   = useState(null);
  const [mergeTargetId, setMergeTargetId] = useState("");
  const [mergePreview,  setMergePreview]  = useState(null);
  const [mergeDelete,   setMergeDelete]   = useState(false);
  const [mergeBusy,     setMergeBusy]     = useState(false);
  const [brandTarget, setBrandTarget] = useState(null);
  const [brandForm, setBrandForm] = useState({ enabled: false, brand_name: "", brand_subtitle: "", primary_color: "#22c55e", logo_url: "", logo_url_light: "", logo_url_dark: "" });

  const [usersTarget, setUsersTarget] = useState(null);
  const [partnerUsers, setPartnerUsers] = useState([]);
  const userSort = useUserSort(partnerUsers);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [addingUser, setAddingUser] = useState(false);
  const [welcomingUserId, setWelcomingUserId] = useState(null);
  const [bulkWelcoming, setBulkWelcoming] = useState(false);
  const unwelcomedCount = partnerUsers.filter((u) => !u.welcome_sent_at).length;
  const sendWelcome = async (u) => {
    setWelcomingUserId(u.user_id);
    try {
      const { data } = await api.post(`/users/${u.user_id}/welcome`);
      toast.success(`Welcome email sent to ${data.email || u.email}`, { description: "Link expires in 24 hours." });
      reloadUsers();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send welcome email");
    } finally {
      setWelcomingUserId(null);
    }
  };
  const bulkWelcome = async () => {
    if (!usersTarget?.org_id) return;
    setBulkWelcoming(true);
    try {
      const { data } = await api.post(`/organizations/${usersTarget.org_id}/welcome-unwelcomed`);
      const t = data.totals || {};
      toast.success(`Sent ${t.sent || 0} welcome email${t.sent === 1 ? "" : "s"}`, {
        description: [t.skipped ? `${t.skipped} skipped` : null, t.failed ? `${t.failed} failed` : null].filter(Boolean).join(" · ") || "All unwelcomed users invited.",
      });
      reloadUsers();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Bulk welcome failed");
    } finally { setBulkWelcoming(false); }
  };
  const [permsTarget, setPermsTarget] = useState(null);
  const [signOutTarget, setSignOutTarget] = useState(null);
  const [mfaTarget, setMfaTarget] = useState(null);
  const [appsOrgTarget, setAppsOrgTarget] = useState(null);
  const [appsUserTarget, setAppsUserTarget] = useState(null);
  const [appCounts, setAppCounts] = useState({});
  const partnerRoles = roles.filter((r) => r.base_role === "partner");

  const loadAppCounts = useCallback(() => {
    if (!has("apps.edit")) return;
    api.get("/apps").then((r) => {
      const m = {};
      r.data.forEach((a) => (a.allowed_orgs || []).forEach((oid) => { m[oid] = (m[oid] || 0) + 1; }));
      setAppCounts(m);
    }).catch(() => {});
  }, [has]);
  useEffect(() => { loadAppCounts(); }, [loadAppCounts]);

  const canMasquerade = (user?.base_role || user?.role) === "staff" && !user?.masqueraded_by;
  const startMasquerade = async (u) => {
    try {
      await api.post("/auth/masquerade/start", { target_user_id: u.user_id });
      window.location.href = "/dashboard";
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start masquerade"); }
  };

  const openUsers = async (o) => {
    setUsersTarget(o); setPartnerUsers([]); setAddingUser(false); setLoadingUsers(true);
    try { const { data } = await api.get("/users", { params: { organization_id: o.org_id } }); setPartnerUsers(data); }
    catch { toast.error("Could not load users."); }
    finally { setLoadingUsers(false); }
  };
  const reloadUsers = async () => {
    if (!usersTarget) return;
    const { data } = await api.get("/users", { params: { organization_id: usersTarget.org_id } });
    setPartnerUsers(data);
  };
  // Iter 110ai — Called by <AddPartnerUserForm> after a successful POST.
  // Owns the parent-side side effects (close form, reload lists) so the
  // child stays cheap and self-contained.
  const onUserCreated = useCallback(async () => {
    setAddingUser(false);
    await reloadUsers();
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usersTarget?.org_id]);

  // Iter 110i — Edit-partner-user modal state + saver. Previously the
  // Partners page had no edit affordance for partner users at all —
  // territory couldn't be set anywhere in the UI. Now every partner
  // row has an "Edit user" action that opens this dialog.
  const canSetQuoter = user?.role === "super_admin" || has("quotes.edit");
  const canChangeRole = user?.role === "super_admin" || has("user_roles.edit");
  const [editingUser, setEditingUser] = useState(null);       // null | user object
  const preview = usePermissionPreview();
  const [editForm, setEditForm] = useState({ name: "", phone: "", role: "partner", territory: "", quoter_username: "" });
  const [savingEdit, setSavingEdit] = useState(false);
  const openEditUser = (u) => {
    setEditingUser(u);
    setEditForm({
      name:      u.name || "",
      phone:     u.phone || "",
      role:      u.role || "partner",
      territory: u.territory || "",
      quoter_username: u.quoter_username || "",
    });
  };
  const closeEditUser = () => { setEditingUser(null); };
  const saveEditUser = async () => {
    if (!editForm.name.trim()) { toast.error("Name is required"); return; }
    setSavingEdit(true);
    if (canChangeRole && editForm.role !== editingUser.role && !(await preview.confirm(
      `/users/${editingUser.user_id}/permission-preview`, { role: editForm.role }, `Change ${editingUser.name || "this user"}'s role?`))) {
      setSavingEdit(false); return;
    }
    try {
      const { data } = await api.put(`/users/${editingUser.user_id}`, {
        name:      editForm.name.trim(),
        phone:     editForm.phone.trim(),
        ...(canChangeRole ? { role: editForm.role } : {}),
        ...(canSetQuoter ? { quoter_username: (editForm.quoter_username || "").trim() } : {}),
      });
      toast.success("User updated");
      toastAccessReset(data, editingUser.name);
      closeEditUser();
      await reloadUsers();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSavingEdit(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { type: "partner" };
      if (search) params.search = search;
      if (filterPartnerType !== "all") params.partner_type = filterPartnerType;
      if (filterStatus !== "all") params.status = filterStatus;
      if (filterPS !== "all") params.partner_success_id = filterPS;
      const { data } = await api.get("/organizations", { params });
      setOrgs(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [search, filterPartnerType, filterStatus, filterPS]);

  const exportView = async (format, withUsers = false) => {
    setExporting(true);
    try {
      const params = new URLSearchParams({ type: "partner", format });
      if (withUsers) params.set("include_users", "true");
      if (search) params.set("search", search);
      if (filterPartnerType !== "all") params.set("partner_type", filterPartnerType);
      if (filterStatus !== "all") params.set("status", filterStatus);
      if (filterPS !== "all") params.set("partner_success_id", filterPS);
      const { data } = await api.get(`/organizations/export?${params.toString()}`);
      if (!data?.token) throw new Error("No download token");
      await downloadFromUrl(`/files/${data.token}`);
      toast.success(`Exported ${data.count} partner${data.count === 1 ? "" : "s"}${withUsers ? " with their users" : ""} as ${format.toUpperCase()}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Export failed");
    } finally { setExporting(false); }
  };

  const clearFilters = () => { setFilterPartnerType("all"); setFilterStatus("all"); setFilterPS("all"); setSearch(""); setSearchDraft(""); };
  const activeFilterCount = (searchDraft ? 1 : 0) + (filterPartnerType !== "all" ? 1 : 0) + (filterStatus !== "all" ? 1 : 0) + (filterPS !== "all" ? 1 : 0);

  // Apply URL-driven restriction (e.g. `?types=Agent,Master Agent` sent from
  // the Commissions page). Applied client-side on top of the server-side
  // filters so it composes cleanly with search / partner_type / status.
  const displayOrgs = useMemo(() => {
    if (!restrictedTypes) return orgs;
    const wanted = new Set(restrictedTypes.map((t) => t.toLowerCase()));
    return orgs.filter((o) => wanted.has((o.partner_type || "").toLowerCase()));
  }, [orgs, restrictedTypes]);
  const orgSort = useOrgSort(displayOrgs);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  const visibleSelectedCount = useMemo(() => displayOrgs.filter((o) => selected.has(o.org_id)).length, [displayOrgs, selected]);
  const allVisibleSelected = displayOrgs.length > 0 && visibleSelectedCount === displayOrgs.length;
  const someVisibleSelected = visibleSelectedCount > 0 && !allVisibleSelected;
  const toggleSelectAllVisible = () => setSelected((prev) => {
    const next = new Map(prev);
    displayOrgs.forEach((o) => (allVisibleSelected ? next.delete(o.org_id) : next.set(o.org_id, o.name)));
    return next;
  });
  // Iter 110af — Sync `search` from the URL `?q=` param on
  // subsequent navigations (e.g., top-bar Global Search clicked
  // while already on /partners). Otherwise the URL changes but the
  // input keeps the old query and the list stays stale.
  useEffect(() => {
    const qp = searchParams.get("q");
    if (qp && qp !== search) { setSearch(qp); setSearchDraft(qp); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  // A cleared search also drops a deep-link ?q, so a reload doesn't bring it back.
  useEffect(() => {
    if (search || !searchParams.get("q")) return;
    const next = new URLSearchParams(searchParams); next.delete("q");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);
  useEffect(() => { api.get("/meta").then((r) => { setPartnerTypes(r.data.partner_types); setTerritories(r.data.territories || []); }).catch(() => {}); }, []);
  useEffect(() => { api.get("/meta").then((r) => setRoles(r.data.roles)).catch(() => {}); }, []);
  useEffect(() => {
    if (has("organizations.edit")) api.get("/staff/partner-success").then((r) => setPsStaff(r.data)).catch(() => {});
  }, [has]);

  // Load user's saved column preferences for the Partners table.
  useEffect(() => {
    api.get("/me/table-prefs?table=partners")
      .then((r) => setTablePrefs(r.data?.prefs || {}))
      .catch(() => setTablePrefs({}));
  }, []);

  // Iter 107c — Helpdesk (formerly Service Desk) open-ticket counts
  // per partner org.  Falls back to zeroes if the caller doesn't have
  // `helpdesk.view`.  Refetched on mount only — cheap.
  useEffect(() => {
    if (canViewHelpdesk) {
      api.get("/helpdesk/counts-by-org")
        .then((r) => setOpenCounts(r.data?.counts || {}))
        .catch(() => setOpenCounts({}));
    }
    api.get("/organizations/did-counts")
      .then((r) => setDidCounts(r.data?.counts || {}))
      .catch(() => setDidCounts({}));
  }, [canViewHelpdesk]);

  const assignPS = async (org, psId) => {
    try { await api.put(`/organizations/${org.org_id}/assign-ps`, { partner_success_id: psId === "none" ? null : psId }); toast.success("Partner Success updated"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const bulkAssignPS = async () => {
    const org_ids = Array.from(selected.keys());
    if (!org_ids.length) { toast.error("Select at least one partner first"); return; }
    setPsBulkBusy(true);
    try {
      const { data } = await api.post("/organizations/assign-ps-bulk", { partner_success_id: psBulkValue === "none" ? null : psBulkValue, org_ids });
      toast.success(`Updated ${data.modified} partner org(s)`); setPsBulkOpen(false); setSelected(new Map()); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setPsBulkBusy(false); }
  };
  // Iter 310 — bulk partner type for the ticked rows.
  const [typeBulkOpen, setTypeBulkOpen] = useState(false);
  const [typeBulkValue, setTypeBulkValue] = useState("none");
  const [typeBulkBusy, setTypeBulkBusy] = useState(false);
  const bulkSetType = async () => {
    const org_ids = Array.from(selected.keys());
    if (!org_ids.length) { toast.error("Select at least one partner first"); return; }
    setTypeBulkBusy(true);
    try {
      const { data } = await api.post("/organizations/bulk-partner-type", { partner_type: typeBulkValue === "none" ? null : typeBulkValue, org_ids });
      toast.success(`Partner type ${data.partner_type ? `set to ${data.partner_type}` : "cleared"} on ${data.modified} partner org(s)`);
      setTypeBulkOpen(false); setSelected(new Map()); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setTypeBulkBusy(false); }
  };

  const openCreate = () => { setEditing(null); setForm(EMPTY); setTerrCustom(false); setDialogOpen(true); };
  const openEdit = (o) => { setEditing(o); setForm({ ...EMPTY, ...o, type: "partner", partner_type: o.partner_type || "none" }); setTerrCustom(false); setDialogOpen(true); };

  const save = async () => {
    // Iter 119y — Partner Admin editing their own company: narrow payload,
    // self-service endpoint. None of the staff-only commercial fields
    // (tier, tax, territory, partner type) are editable by them.
    if (isPartnerViewer) {
      if (!(form.name || "").trim()) { toast.error("Company name is required."); return; }
      try {
        await api.put("/my-organization", {
          name: form.name, contact_name: form.contact_name || "", contact_email: form.contact_email || "",
          contact_phone: form.contact_phone || "", location: form.location || "", notes: form.notes || "",
        });
        toast.success("Company profile updated");
        setDialogOpen(false); load();
      } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
      return;
    }
    // Coerce empty tax_rate string → null so Pydantic (Optional[float]) accepts
    // it. Empty text fields stay as "" which the schema treats as unset.
    const taxRateNum = form.tax_rate === "" || form.tax_rate === null || form.tax_rate === undefined
      ? null
      : Number(form.tax_rate);
    const payload = {
      ...form,
      type: "partner",
      parent_org_id: null,
      partner_type: form.partner_type !== "none" ? form.partner_type : null,
      tax_rate: Number.isFinite(taxRateNum) ? taxRateNum : null,
    };
    // Iter 110j — Client-side pre-flight for the mandatory Territory
    // field. Matches the backend 400 so the user sees the error
    // immediately without a network round-trip.
    if (payload.type === "partner" && !(payload.territory || "").trim()) {
      toast.error("Territory is required for partner organizations.");
      return;
    }
    payload.territory = (payload.territory || "").trim();
    try {
      if (editing) { await api.put(`/organizations/${editing.org_id}`, payload); toast.success("Partner updated"); }
      else { await api.post("/organizations", payload); toast.success("Partner created"); }
      setDialogOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const doDelete = async () => {
    try { await api.delete(`/organizations/${deleteTarget.org_id}`); toast.success("Partner deleted"); setDeleteTarget(null); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  // Iter 87 — Partner Merge helpers.
  const openMerge = (o) => {
    setMergeSource(o);
    setMergeTargetId("");
    setMergePreview(null);
    setMergeDelete(false);
  };
  const loadMergePreview = async (targetId) => {
    setMergePreview(null);
    setMergeTargetId(targetId);
    if (!targetId || !mergeSource) return;
    try {
      const { data } = await api.get(`/organizations/${mergeSource.org_id}/merge-preview`, { params: { target_id: targetId } });
      setMergePreview(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Preview failed");
    }
  };
  const runMerge = async () => {
    if (!mergeSource || !mergeTargetId) return;
    const t = toast.loading("Merging partner…");
    try {
      setMergeBusy(true);
      const { data } = await api.post(`/organizations/${mergeSource.org_id}/merge-into`, {
        target_org_id: mergeTargetId,
        delete_source: mergeDelete,
      });
      toast.success(
        `Merged into ${data.target?.name || "target"} — ${data.dids_moved.toLocaleString()} DIDs, ${data.aliases_added.length} aliases`,
        { id: t, duration: 5000 }
      );
      setMergeSource(null);
      setMergeTargetId("");
      setMergePreview(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Merge failed", { id: t });
    } finally { setMergeBusy(false); }
  };

  const openBranding = (o) => {
    setBrandForm(brandingFormOf(o));
    setBrandTarget(o);
  };
  // Iter 85: partners in these tiers can't brand — hidden for partner
  // callers, but staff can still open the dialog (server enforces).
  const brandingLockedTiers = new Set(["referral", "agent", "master_agent", "master agent"]);
  const isBrandingLocked = (o) => {
    const t = (o?.commission_tier || "").toLowerCase();
    const s = (o?.status || "").toLowerCase();
    return brandingLockedTiers.has(t) || brandingLockedTiers.has(s);
  };
  const callerIsStaff = (user?.base_role || user?.role) === "staff" || user?.role === "super_admin";
  const saveBranding = async () => {
    try {
      // Iter 119y — Partner Admins hit the self-service endpoint (hard-scoped
      // to their own org); staff use the org-targeted route.
      const url = isPartnerViewer ? "/my-organization/branding" : `/organizations/${brandTarget.org_id}/branding`;
      await api.put(url, brandForm); toast.success("Branding updated"); setBrandTarget(null); load();
    }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  // --- Column config for the Partners table -------------------------------
  // Order in this array defines the default order. `always` columns cannot
  // be hidden or reordered (name + row actions).
  // Iter 110ai — Memoise so we don't rebuild this array on every
  // keystroke; downstream `displayCols`, `visibleIds`, `defaultVisibleIds`
  // all depend on it and used to recompute on every render.
  const COLUMNS = useMemo(() => [
    ...(canManagePS && !isPartnerViewer ? [{ id: "select", label: "Select", always: true }] : []),
    { id: "name",           label: "Partner",         always: true },
    { id: "partner_type",   label: "Partner Type" },
    { id: "partner_success",label: "Partner Success" },
    { id: "contact",        label: "Contact" },
    { id: "users",          label: "Users" },
    // Iter 115q — Helpdesk + QBR are hidden from the partner-facing "Staff"
    // view (more work needed before partners see either); both remain
    // permission-gated for staff viewers.
    // Iter 115s — BACKLOG: Helpdesk + QBR columns are hidden for EVERY user
    // until those apps are ready. Flip HELPDESK_QBR_COLUMNS_ENABLED to bring
    // them back (permission gates below still apply).
    ...(HELPDESK_QBR_COLUMNS_ENABLED && canViewHelpdesk && !isPartnerViewer ? [{ id: "service_desk", label: "Helpdesk" }] : []),
    ...(HELPDESK_QBR_COLUMNS_ENABLED && canViewQbr && !isPartnerViewer ? [{ id: "qbr", label: "QBR" }] : []),
    { id: "status",         label: "Status" },
    { id: "actions",        label: "",              always: true },
  ], [canViewQbr, canViewHelpdesk, isPartnerViewer, canManagePS]);
  const defaultVisibleIds = useMemo(
    () => COLUMNS.map((c) => c.id),
    [COLUMNS],
  );
  const visibleIds = useMemo(() => (
    Array.isArray(tablePrefs.visible) && tablePrefs.visible.length
      ? [
          ...COLUMNS.filter((c) => c.always && !tablePrefs.visible.includes(c.id)).map((c) => c.id),
          ...tablePrefs.visible.filter((id) => COLUMNS.some((c) => c.id === id)),
          ...COLUMNS.filter((c) => c.always && tablePrefs.visible.includes(c.id) && false).map((c) => c.id),
        ]
      : defaultVisibleIds
  ), [tablePrefs.visible, COLUMNS, defaultVisibleIds]);
  const displayCols = useMemo(
    () => visibleIds.map((id) => COLUMNS.find((c) => c.id === id)).filter(Boolean),
    [visibleIds, COLUMNS],
  );
  const saveTablePrefs = async (nextVisible) => {
    const nextPrefs = nextVisible === null ? {} : { ...tablePrefs, visible: nextVisible };
    setTablePrefs(nextPrefs);
    try { await api.put("/me/table-prefs", { table: "partners", prefs: nextPrefs }); } catch { /* silent */ }
  };
  const toggleCol = (id) => {
    if (COLUMNS.find((c) => c.id === id)?.always) return;
    const cur = visibleIds.slice();
    if (cur.includes(id)) saveTablePrefs(cur.filter((x) => x !== id));
    else {
      // Insert before the trailing "actions" column so it stays last.
      const insertAt = cur.indexOf("actions");
      const next = insertAt < 0 ? [...cur, id] : [...cur.slice(0, insertAt), id, ...cur.slice(insertAt)];
      saveTablePrefs(next);
    }
  };
  const moveCol = (id, dir) => {
    const cur = visibleIds.slice();
    const idx = cur.indexOf(id);
    if (idx < 0) return;
    const nextIdx = idx + dir;
    if (nextIdx < 0 || nextIdx >= cur.length) return;
    // Don't allow crossing an always-column boundary (name at start, actions at end).
    const swapWith = cur[nextIdx];
    if (COLUMNS.find((c) => c.id === swapWith)?.always) return;
    cur.splice(idx, 1);
    cur.splice(nextIdx, 0, id);
    saveTablePrefs(cur);
  };

  const renderCell = (col, o) => {
    switch (col.id) {
      case "select":
        return (
          <TableCell className="w-10 pr-0">
            <Checkbox checked={selected.has(o.org_id)} onCheckedChange={() => toggleSelected(o)}
              aria-label={`Select ${o.name}`} data-testid={`partner-select-${o.org_id}`} />
          </TableCell>
        );
      case "name": {
        const isCommissionable = ["Agent", "Master Agent", "Referral"].includes(o.partner_type || "");
        return (
          <TableCell className="py-3">
            <div className="flex items-center gap-3">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#fef3c7] text-[#b45309]"><Handshake className="h-4 w-4" /></span>
              <div className="min-w-0">
                <span className="text-sm font-medium text-slate-800">{o.name}</span>
                {has("apps.edit") && appCounts[o.org_id] > 0 && (
                  <button data-testid={`partner-app-count-${o.org_id}`} onClick={() => setAppsOrgTarget({ id: o.org_id, name: o.name })}
                    className="ml-2 inline-flex items-center gap-1 rounded-full bg-[#eef2fb] px-1.5 py-0.5 align-middle text-[10px] font-medium text-primary hover:bg-[#e2e9f9]">
                    <AppWindow className="h-3 w-3" /> {appCounts[o.org_id]} app{appCounts[o.org_id] > 1 ? "s" : ""}
                  </button>
                )}
                {/* Iter 110o — Partner-row asset pills (Commissions,
                    DIDs, tbd Domains, tbd Extensions) hidden until
                    Phase 2. Backlog: resurface as one block when the
                    PBX sync + Commissions revamp ships. Do NOT delete
                    — the click handlers still work; just gate on
                    false so the row stays clean. */}
                {false && isCommissionable && (
                  <Link
                    to={`/commissions?q=${encodeURIComponent(o.commission_alias || o.name)}`}
                    data-testid={`partner-commissions-link-${o.org_id}`}
                    title={`Open ${o.name} in Commissions`}
                    className="ml-2 inline-flex items-center gap-1 rounded-full bg-emerald-50 px-1.5 py-0.5 align-middle text-[10px] font-medium text-emerald-700 hover:bg-emerald-100"
                  >
                    <DollarSign className="h-3 w-3" /> Commissions
                  </Link>
                )}
                {false && didCounts[o.org_id] > 0 && (
                  <Link
                    to={`/did-master?partner=${encodeURIComponent(o.org_id)}`}
                    data-testid={`partner-did-count-${o.org_id}`}
                    title={`${didCounts[o.org_id]} DID${didCounts[o.org_id] === 1 ? "" : "s"} owned by ${o.name}`}
                    className="ml-2 inline-flex items-center gap-1 rounded-full bg-sky-50 px-1.5 py-0.5 align-middle text-[10px] font-medium text-sky-700 hover:bg-sky-100"
                  >
                    <Phone className="h-3 w-3" /> {didCounts[o.org_id].toLocaleString()} DID{didCounts[o.org_id] === 1 ? "" : "s"}
                  </Link>
                )}
                {/* PBX-sourced pills — counts arrive from the PBX API later.
                    For now numeric value is 'tbd' and clicks are inert. The
                    Extensions count will exclude the 5000-5999 range once
                    the API is wired. */}
                {false && (
                  <span data-testid={`partner-domains-pill-${o.org_id}`}
                        title="Domains — PBX sync coming soon"
                        className="ml-2 inline-flex items-center gap-1 rounded-full bg-slate-100 px-1.5 py-0.5 align-middle text-[10px] font-medium text-slate-500">
                    <Globe className="h-3 w-3" /> tbd Domains
                  </span>
                )}
                {false && (
                  <span data-testid={`partner-extensions-pill-${o.org_id}`}
                        title="Extensions (excludes 5000-5999) — PBX sync coming soon"
                        className="ml-2 inline-flex items-center gap-1 rounded-full bg-slate-100 px-1.5 py-0.5 align-middle text-[10px] font-medium text-slate-500">
                    <Hash className="h-3 w-3" /> tbd Extensions
                  </span>
                )}
                <SearchMatchHint match={o.search_match} testid={`partner-search-match-${o.org_id}`} />
              </div>
            </div>
          </TableCell>
        );
      }
      case "partner_type":
        return <TableCell>{o.partner_type ? <span className="inline-flex rounded-md bg-[#eef2fb] px-2 py-0.5 text-xs font-medium text-primary">{o.partner_type}</span> : <span className="text-slate-300">—</span>}</TableCell>;
      case "partner_success":
        return (
          <TableCell>
            {canManagePS ? (
              <Select value={o.partner_success_id || "none"} onValueChange={(v) => assignPS(o, v)}>
                <SelectTrigger data-testid={`ps-assign-${o.org_id}`} className="h-8 w-44"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Unassigned</SelectItem>
                  {psStaff.map((p) => <SelectItem key={p.user_id} value={p.user_id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            ) : <span className="text-sm text-slate-600">{o.partner_success_name || "Unassigned"}</span>}
          </TableCell>
        );
      case "contact":
        return <TableCell className="text-sm text-slate-600">{o.contact_name ? <div>{o.contact_name}<div className="text-xs text-slate-400">{o.contact_email}</div></div> : "—"}</TableCell>;
      case "users":
        return (
          <TableCell>
            <button data-testid={`partner-users-${o.org_id}`} onClick={() => openUsers(o)} className="inline-flex items-center gap-1.5 rounded-md bg-[#eef2fb] px-2 py-1 text-xs font-medium text-primary hover:bg-[#e2e9f9]">
              <UsersIcon className="h-3.5 w-3.5" /> {o.user_count ?? 0}
            </button>
          </TableCell>
        );
      case "service_desk": {
        // Iter 108g — Show open helpdesk tickets with an overdue tint
        // when any SLA has burned. Clicking jumps to the Kanban board
        // pre-filtered to this partner.
        const row     = openCounts[o.org_id] || {};
        const cnt     = row.open_tickets || 0;
        const overdue = row.overdue_tickets || 0;
        return (
          <TableCell>
            <button data-testid={`partner-sd-count-${o.org_id}`}
              onClick={() => navigate(`/helpdesk?org=${o.org_id}`)}
              className={`inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium transition-colors ${
                overdue > 0
                  ? "border border-red-300 bg-red-50 text-red-800 hover:bg-red-100"
                  : cnt > 0
                    ? "border border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                    : "border border-slate-200 bg-slate-50 text-slate-500 hover:bg-slate-100"
              }`}
              title={overdue > 0
                ? `${cnt} open · ${overdue} overdue`
                : cnt > 0
                  ? `${cnt} open ticket${cnt === 1 ? "" : "s"}`
                  : "Open Helpdesk for this partner"}>
              <MessageSquare className="h-3.5 w-3.5" /> {cnt}
              {overdue > 0 && (
                <span className="rounded-full bg-red-600/90 px-1.5 text-[10px] font-semibold text-white">{overdue}</span>
              )}
            </button>
          </TableCell>
        );
      }
      case "qbr":
        return (
          <TableCell>
            <button data-testid={`partner-qbr-${o.org_id}`} onClick={() => navigate(`/qbr?partner=${o.org_id}`)}
              className="inline-flex items-center gap-1.5 rounded-md border border-indigo-200 bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700 transition-colors hover:bg-indigo-100">
              <BarChart3 className="h-3.5 w-3.5" /> View QBR
            </button>
          </TableCell>
        );
      case "status":
        return <TableCell><StatusChip status={o.status} data-testid={`partner-status-${o.org_id}`} /></TableCell>;
      case "actions": {
        const isCommissionable = ["Agent", "Master Agent", "Referral"].includes(o.partner_type || "");
        return (
          <TableCell>
            {(has("organizations.edit") || has("organizations.delete") || (isCommissionable && has("commissions.view"))) && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" data-testid={`partner-actions-${o.org_id}`} className="h-8 w-8"><MoreHorizontal className="h-4 w-4" /></Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {has("users.view") && <DropdownMenuItem onClick={() => openUsers(o)}><UsersIcon className="h-4 w-4" /> Manage users</DropdownMenuItem>}
                  {isCommissionable && has("commissions.view") && (
                    <DropdownMenuItem
                      onClick={() => navigate(`/commissions?q=${encodeURIComponent(o.commission_alias || o.name)}`)}
                      data-testid={`partner-commissions-menu-${o.org_id}`}>
                      <DollarSign className="h-4 w-4" /> View commissions
                    </DropdownMenuItem>
                  )}
                  {isCommissionable && has("commissions.edit") && (
                    <DropdownMenuItem
                      onClick={() => navigate(`/commissions/partners/${encodeURIComponent(o.commission_alias || o.name)}`)}
                      data-testid={`partner-commissions-details-menu-${o.org_id}`}>
                      <BarChart3 className="h-4 w-4" /> View commission details
                    </DropdownMenuItem>
                  )}
                  {has("organizations.edit") && <DropdownMenuItem onClick={() => openEdit(o)}><Pencil className="h-4 w-4" /> Edit</DropdownMenuItem>}
                  {has("helpdesk.view") && helpdeskLive && <DropdownMenuItem onClick={() => navigate(`/helpdesk?org=${o.org_id}`)} data-testid={`partner-tickets-${o.org_id}`}><LifeBuoy className="h-4 w-4" /> Helpdesk tickets</DropdownMenuItem>}
                  {has("organizations.edit") && (callerIsStaff || !isBrandingLocked(o)) && <DropdownMenuItem onClick={() => openBranding(o)} data-testid={`partner-branding-${o.org_id}`}><Palette className="h-4 w-4" /> Branding{isBrandingLocked(o) && <span className="ml-auto text-[10px] text-amber-600">(staff-only)</span>}{!isBrandingLocked(o) && o.branding?.enabled && <span className="ml-auto h-2 w-2 rounded-full" style={{ background: o.branding.primary_color }} />}</DropdownMenuItem>}
                  {has("apps.edit") && <DropdownMenuItem onClick={() => setAppsOrgTarget({ id: o.org_id, name: o.name })} data-testid={`partner-apps-${o.org_id}`}><AppWindow className="h-4 w-4" /> Manage apps</DropdownMenuItem>}
                  {user?.role === "super_admin" && <DropdownMenuItem onClick={() => openMerge(o)} data-testid={`partner-merge-${o.org_id}`}><GitMerge className="h-4 w-4" /> Merge into…</DropdownMenuItem>}
                  {has("organizations.delete") && <DropdownMenuItem onClick={() => setDeleteTarget(o)} className="text-red-600 focus:text-red-600"><Trash2 className="h-4 w-4" /> Delete</DropdownMenuItem>}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </TableCell>
        );
      }
      default:
        return null;
    }
  };

  // Iter 111 — For partner-role viewers, auto-open the users list for
  // their own org and render this page as their team roster. They can
  // only ever see one partner (their own), so the multi-partner table
  // + filters + widgets are hidden entirely.
  useEffect(() => {
    if (!isPartnerViewer) return;
    if (!Array.isArray(orgs) || orgs.length === 0) return;
    if (usersTarget) return;
    // The API already scopes /organizations to a partner's own org
    // when they call it, so `orgs[0]` is their partner. Open it.
    openUsers(orgs[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPartnerViewer, orgs, usersTarget]);

  // Iter 119y — Deep links from the avatar menu: `/partners?action=edit`
  // opens the company profile dialog, `?action=branding` opens branding —
  // both always targeting the partner's OWN org. The param is stripped
  // straight after opening, which re-runs this effect as a no-op and leaves
  // the link re-usable from the Partners page itself.
  useEffect(() => {
    if (!isPartnerViewer) return;
    const action = searchParams.get("action");
    if (!action || !Array.isArray(orgs) || orgs.length === 0) return;
    if (action === "edit") openEdit(orgs[0]);
    else if (action === "branding") { navigate("/settings?tab=branding"); return; }
    const next = new URLSearchParams(searchParams);
    next.delete("action");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPartnerViewer, orgs, searchParams]);

  return (
    <div>
      <PageHeader
        testid="partners-header"
        title={isPartnerViewer ? "Staff" : "Partners"}
        subtitle={isPartnerViewer ? "Your team members and their access." : "Partner accounts, channel types and Partner Success ownership."}
      >
        {!isPartnerViewer && canManagePS && (
          <>
            {selected.size > 0 && (
              <span className="inline-flex h-9 items-center gap-1.5 rounded-md border border-emerald-200 bg-emerald-50 px-2.5 text-xs font-medium text-emerald-700" data-testid="partners-selected-chip">
                {selected.size} selected
                <button type="button" onClick={() => setSelected(new Map())} className="rounded p-0.5 text-emerald-500 hover:text-emerald-800" title="Clear selection" data-testid="partners-selected-clear">
                  <XIcon className="h-3 w-3" />
                </button>
              </span>
            )}
            <Button variant="outline" data-testid="assign-type-bulk-button" disabled={selected.size === 0}
              title={selected.size === 0 ? "Tick one or more partners in the table first" : undefined}
              onClick={() => setTypeBulkOpen(true)}>
              <Tag className="h-4 w-4" /> Set partner type{selected.size > 0 ? ` (${selected.size})` : ""}
            </Button>
            <Button variant="outline" data-testid="assign-ps-bulk-button" disabled={selected.size === 0}
              title={selected.size === 0 ? "Tick one or more partners in the table first" : undefined}
              onClick={() => setPsBulkOpen(true)}>
              <UserCheck className="h-4 w-4" /> Assign Partner Success{selected.size > 0 ? ` (${selected.size})` : ""}
            </Button>
          </>
        )}
        {!isPartnerViewer && has("organizations.create") && (
          <Button data-testid="add-partner-button" onClick={openCreate} className="bg-primary hover:bg-primary/90 text-primary-foreground">
            <Plus className="h-4 w-4" /> Add partner
          </Button>
        )}
      </PageHeader>

      {/* Iter 90c — Moved from Dashboard. Scoped to super admin + Partner-team staff.
          Iter 306 — "Commissions this cycle" removed at Julian's request; readiness stays.
          Widget ships its own Card so we use variant="bare" to avoid double-borders. */}
      {has("commissions.view") && (user?.role === "super_admin"
        || (user?.base_role === "staff" && (user?.team || "").toLowerCase() === "partner")) && (
        <div className="mt-2" data-testid="partners-summary-widgets">
          <CollapsibleWidgetShell
            variant="bare"
            storageKey="partners_readiness_open" defaultOpen
            title="Partner readiness"
            testid="collapsible-readiness">
            <PartnerReadinessWidget />
          </CollapsibleWidgetShell>
        </div>
      )}

      {/* Iter 111 — Partner-role viewers see only their team roster
          (rendered via the inline users panel below). Multi-partner
          filters + table are hidden for them AND for staff viewers
          drilled into a specific org via `usersTarget`. */}
      {!isPartnerViewer && !usersTarget && (
      <>
      <div className="mb-4 flex flex-wrap items-end gap-2">
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input data-testid="partners-search" value={searchDraft} onChange={(e) => setSearchDraft(e.target.value)} placeholder="Search partners, contacts, users, territory…" className="pl-9" />
        </div>
        <div className="min-w-[160px]">
          <Label className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-slate-400">Partner type</Label>
          <Select value={filterPartnerType} onValueChange={setFilterPartnerType}>
            <SelectTrigger data-testid="partners-filter-type" className="h-9 w-full text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              <SelectItem value="unassigned">Unassigned</SelectItem>
              {partnerTypes.map((pt) => <SelectItem key={pt} value={pt}>{pt}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[130px]">
          <Label className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-slate-400">Status</Label>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger data-testid="partners-filter-status" className="h-9 w-full text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="suspended">Suspended</SelectItem>
              <SelectItem value="archived">Archived</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {canManagePS && (
          <div className="min-w-[180px]">
            <Label className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-slate-400">Partner Success</Label>
            <Select value={filterPS} onValueChange={setFilterPS}>
              <SelectTrigger data-testid="partners-filter-ps" className="h-9 w-full text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Partner Success</SelectItem>
                <SelectItem value="unassigned">Unassigned</SelectItem>
                {psStaff.map((p) => <SelectItem key={p.user_id} value={p.user_id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        {activeFilterCount > 0 && (
          <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="partners-filter-clear"
            className="h-9 self-end text-slate-500 hover:text-slate-700">
            <XIcon className="h-3.5 w-3.5" /> Clear ({activeFilterCount})
          </Button>
        )}
        {restrictedTypes && (
          <div className="flex h-9 items-center gap-2 self-end rounded-md border border-emerald-200 bg-emerald-50 px-2.5 text-xs text-emerald-700"
               data-testid="partners-restricted-pill">
            <span className="font-medium">Showing only:</span>
            <span>{restrictedTypes.join(" + ")}</span>
            <button onClick={clearRestriction} data-testid="partners-restricted-clear"
                    className="rounded p-0.5 text-emerald-500 hover:text-emerald-800"
                    title="Show all partner types">
              <XIcon className="h-3 w-3" />
            </button>
          </div>
        )}
        <div className="ml-auto flex items-end gap-1.5 self-end">
          <ColumnsToolbar
            testidPrefix="partners"
            count={displayOrgs.length}
            loading={loading}
            columns={COLUMNS}
            visibleIds={visibleIds}
            onToggle={toggleCol}
            onMove={moveCol}
            onReset={() => saveTablePrefs(null)}
            onExportCsv={() => exportView("csv")}
            onExportCsvWithUsers={() => exportView("csv", true)}
            onExportPdf={() => exportView("pdf")}
            exporting={exporting}
          />
        </div>
      </div>

      <Card className="border-slate-200 shadow-sm">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              {displayCols.map((c) => (
                <TableHead key={c.id} className={c.id === "actions" ? "w-12" : c.id === "select" ? "w-10 pr-0" : undefined} data-testid={`partners-col-${c.id}`}>
                  {c.id === "select" ? (
                    <Checkbox
                      checked={allVisibleSelected ? true : someVisibleSelected ? "indeterminate" : false}
                      disabled={displayOrgs.length === 0}
                      onCheckedChange={toggleSelectAllVisible}
                      aria-label="Select all filtered partners"
                      data-testid="partners-select-all"
                    />
                  ) : PARTNER_SORTABLE.includes(c.id) ? (
                    <SortHeader label={c.label} sortKey={c.id} sort={orgSort.sort} onToggle={orgSort.toggle}
                      className="normal-case tracking-normal" testid={`partners-sort-${c.id}`} />
                  ) : c.id === "name" ? (
                    <>{c.label} <span className="ml-1 text-[11px] font-normal"><NameSortHint sort={orgSort.sort} byLast={false} testid="partners-name-order" /></span></>
                  ) : c.label}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={displayCols.length} className="py-10 text-center text-slate-400">Loading…</TableCell></TableRow>
            ) : displayOrgs.length === 0 ? (
              <TableRow><TableCell colSpan={displayCols.length} className="py-10 text-center text-slate-400" data-testid="partners-empty">No partners found</TableCell></TableRow>
            ) : (
              orgSort.sorted.map((o) => (
                <TableRow key={o.org_id} data-testid={`partner-row-${o.org_id}`}>
                  {displayCols.map((c) => <React.Fragment key={c.id}>{renderCell(c, o)}</React.Fragment>)}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>
      </>
      )}
      {/* Partner viewers see a small "empty" hint if they close the
          auto-opened team dialog — they can nav back via the top-bar. */}
      {isPartnerViewer && !usersTarget && (
        <div className="mt-6 rounded-lg border border-dashed border-slate-200 bg-slate-50/50 p-6 text-center text-sm text-slate-500" data-testid="staff-empty-hint">
          Your team roster loads automatically. If you closed it, refresh the page or navigate back to the dashboard.
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="flex max-h-[85vh] max-w-md flex-col gap-0 p-0" data-testid="partner-edit-dialog">
          <DialogHeader className="border-b border-border px-6 py-4">
            <DialogTitle>{isPartnerViewer ? "Edit company profile" : editing ? "Edit partner" : "Add partner"}</DialogTitle>
          </DialogHeader>
          <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-6 py-4" data-testid="partner-edit-scroll">
            <div className="space-y-1.5"><Label>{isPartnerViewer ? "Company name" : "Name"}</Label>
              <Input data-testid="partner-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            {!isPartnerViewer && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Partner type</Label>
                <Select value={form.partner_type} onValueChange={(v) => setForm({ ...form, partner_type: v })}>
                  <SelectTrigger data-testid="partner-type-select"><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Unspecified</SelectItem>
                    {partnerTypes.map((pt) => <SelectItem key={pt} value={pt} data-testid={`partner-type-${pt.replace(/\s+/g, '-').toLowerCase()}`}>{pt}</SelectItem>)}
                  </SelectContent>
                </Select></div>
              <div className="space-y-1.5"><Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="partner-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="suspended">Suspended</SelectItem><SelectItem value="deactivated">Deactivated</SelectItem></SelectContent>
                </Select></div>
            </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Contact name</Label><Input data-testid="partner-contact-name" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Contact phone</Label><Input data-testid="partner-contact-phone" value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5"><Label>Contact email</Label><Input data-testid="partner-contact-email" type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} /></div>
            {/* Iter 110j — Territory. Mandatory for partner-type orgs.
                Inherited by every user in this org for Audit Log scoping,
                so one edit here retargets all of the partner's users.
                Iter 111 — Now a curated dropdown (super-admin managed
                via Settings → Marketing → Territories) with a "Custom /
                Other" escape hatch so a brand-new territory can still
                be entered without a settings edit first. */}
            {form.type === "partner" && !isPartnerViewer && (() => {
              const currentVal = (form.territory || "").trim();
              const inList = currentVal && territories.includes(currentVal);
              // Sticky custom mode: derived-only state collapsed the
              // moment territory was blanked, so the input never showed.
              const customActive = terrCustom || (currentVal && !inList);
              const selectValue = customActive ? "__custom__" : (currentVal || "");
              return (
                <div className="space-y-1.5" data-testid="partner-territory-row">
                  <Label>Territory <span className="text-red-500">*</span></Label>
                  <Select
                    value={selectValue}
                    onValueChange={(v) => {
                      if (v === "__custom__") {
                        // Blank the field so the operator has to type
                        // a value — prevents accidentally saving the
                        // literal string "Custom / Other".
                        setTerrCustom(true);
                        setForm({ ...form, territory: "" });
                      } else {
                        setTerrCustom(false);
                        setForm({ ...form, territory: v });
                      }
                    }}
                  >
                    <SelectTrigger data-testid="partner-territory-select">
                      <SelectValue placeholder="Pick a territory…" />
                    </SelectTrigger>
                    <SelectContent>
                      {territories.map((t) => (
                        <SelectItem key={t} value={t} data-testid={`partner-territory-opt-${t}`}>{t}</SelectItem>
                      ))}
                      <SelectItem value="__custom__" data-testid="partner-territory-custom">
                        Custom / Other…
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  {customActive && (
                    <Input
                      data-testid="partner-territory-input"
                      placeholder="e.g. ngenuity.ca"
                      value={form.territory || ""}
                      onChange={(e) => setForm({ ...form, territory: e.target.value })}
                    />
                  )}
                  <div className="text-[11px] text-slate-400">
                    Scopes the Audit Log to rows where <code className="font-mono">territory</code> equals this value.
                    Every user assigned to this partner inherits it — edit once here to retarget everyone.
                    Missing an option? Super-admins can add it under Settings → Marketing → Territories.
                  </div>
                </div>
              );
            })()}
            {!isPartnerViewer && (<>
            <div className="space-y-1.5">
              <Label>Commission alias <span className="text-xs font-normal text-slate-400">(optional)</span></Label>
              <Input data-testid="partner-commission-alias" value={form.commission_alias || ""}
                     placeholder={form.name ? `Defaults to "${form.name}"` : "e.g. Kobelt Development"}
                     onChange={(e) => setForm({ ...form, commission_alias: e.target.value })} />
              <div className="text-[11px] text-slate-400">
                Override how this partner is matched in the Stax commission export.
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label>Jurisdiction</Label>
                <Select
                  value={CA_TAX_PRESETS.find((p) => p.value === form.tax_jurisdiction)?.code || (form.tax_jurisdiction ? "OTHER" : "NONE")}
                  onValueChange={(code) => {
                    if (code === "NONE") {
                      setForm({ ...form, tax_jurisdiction: "", tax_rate: "" });
                    } else if (code === "OTHER") {
                      setForm({ ...form, tax_jurisdiction: form.tax_jurisdiction && !CA_TAX_PRESETS.some(p => p.value === form.tax_jurisdiction) ? form.tax_jurisdiction : "" });
                    } else {
                      const preset = CA_TAX_PRESETS.find((p) => p.code === code);
                      if (preset) setForm({ ...form, tax_jurisdiction: preset.value, tax_rate: preset.rate });
                    }
                  }}>
                  <SelectTrigger data-testid="partner-tax-jurisdiction-select">
                    <SelectValue placeholder="Choose a province…" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="NONE">— None (not taxable) —</SelectItem>
                    {CA_TAX_PRESETS.map((p) => (
                      <SelectItem key={p.code} value={p.code} data-testid={`partner-tax-jur-${p.code}`}>
                        {p.label}
                      </SelectItem>
                    ))}
                    <SelectItem value="OTHER">Custom / Other</SelectItem>
                  </SelectContent>
                </Select>
                {(!CA_TAX_PRESETS.some((p) => p.value === form.tax_jurisdiction) && form.tax_jurisdiction !== "") && (
                  <Input data-testid="partner-tax-jurisdiction" value={form.tax_jurisdiction || ""}
                         placeholder="e.g. NY · Sales Tax"
                         onChange={(e) => setForm({ ...form, tax_jurisdiction: e.target.value })} />
                )}
              </div>
              <div className="space-y-1.5">
                <Label>Tax rate %</Label>
                <Input data-testid="partner-tax-rate" type="number" step="0.001"
                       value={form.tax_rate ?? ""}
                       placeholder="5.00"
                       onChange={(e) => setForm({ ...form, tax_rate: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Tax registration #</Label>
                <Input data-testid="partner-tax-number" value={form.tax_number || ""}
                       placeholder="Optional"
                       onChange={(e) => setForm({ ...form, tax_number: e.target.value })} />
              </div>
            </div>
            <div className="text-[11px] text-slate-400 -mt-2">
              Used to generate pro-forma commission invoices with the correct sales tax line.
            </div>
            </>)}
            <div className="space-y-1.5"><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} /></div>
          </div>
          <DialogFooter className="border-t border-border px-6 py-4">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button data-testid="save-partner-button" onClick={save} className="bg-primary hover:bg-primary/90 text-primary-foreground">{isPartnerViewer || editing ? "Save" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={psBulkOpen} onOpenChange={setPsBulkOpen}>
        <DialogContent className="max-w-md" data-testid="ps-bulk-dialog">
          <DialogHeader><DialogTitle>Assign Partner Success to {selected.size} selected partner{selected.size === 1 ? "" : "s"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-500">Only the ticked partners below are changed. Choose “Unassigned” to clear their Partner Success owner.</p>
            <div className="max-h-40 overflow-y-auto rounded-md border border-slate-200 bg-slate-50/60 px-3 py-2 text-xs text-slate-600" data-testid="ps-bulk-selected-list">
              {Array.from(selected.entries()).map(([id, name]) => (
                <div key={id} className="truncate py-0.5" data-testid={`ps-bulk-selected-${id}`}>{name}</div>
              ))}
            </div>
            <div className="space-y-1.5">
              <Label>Partner Success owner</Label>
              <Select value={psBulkValue} onValueChange={setPsBulkValue}>
                <SelectTrigger data-testid="ps-bulk-select"><SelectValue placeholder="Select staff" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Unassigned (clear)</SelectItem>
                  {psStaff.map((p) => <SelectItem key={p.user_id} value={p.user_id}>{p.name} · {p.role_label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPsBulkOpen(false)}>Cancel</Button>
            <Button data-testid="ps-bulk-confirm" onClick={bulkAssignPS} disabled={psBulkBusy || selected.size === 0} className="bg-primary hover:bg-primary/90 text-primary-foreground">
              {psBulkBusy && <Loader2 className="h-4 w-4 animate-spin" />}
              Apply to {selected.size} selected
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={typeBulkOpen} onOpenChange={setTypeBulkOpen}>
        <DialogContent className="max-w-md" data-testid="type-bulk-dialog">
          <DialogHeader><DialogTitle>Set partner type on {selected.size} selected partner{selected.size === 1 ? "" : "s"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-500">Only the ticked partners below are changed. Partner type drives commissions eligibility (Agent, Master Agent, Referral) and audience targeting.</p>
            <div className="max-h-40 overflow-y-auto rounded-md border border-slate-200 bg-slate-50/60 px-3 py-2 text-xs text-slate-600" data-testid="type-bulk-selected-list">
              {Array.from(selected.entries()).map(([id, name]) => (
                <div key={id} className="truncate py-0.5">{name}</div>
              ))}
            </div>
            <div className="space-y-1.5">
              <Label>Partner type</Label>
              <Select value={typeBulkValue} onValueChange={setTypeBulkValue}>
                <SelectTrigger data-testid="type-bulk-select"><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Unassigned (clear)</SelectItem>
                  {partnerTypes.map((pt) => <SelectItem key={pt} value={pt} data-testid={`type-bulk-option-${pt.replace(/\s+/g, "-").toLowerCase()}`}>{pt}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTypeBulkOpen(false)}>Cancel</Button>
            <Button data-testid="type-bulk-confirm" onClick={bulkSetType} disabled={typeBulkBusy || selected.size === 0} className="bg-primary hover:bg-primary/90 text-primary-foreground">
              {typeBulkBusy && <Loader2 className="h-4 w-4 animate-spin" />}
              Apply to {selected.size} selected
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={brandTarget !== null} onOpenChange={(o) => !o && setBrandTarget(null)}>
        <DialogContent className="flex max-h-[85vh] max-w-md flex-col gap-0 p-0" data-testid="partner-branding-dialog">
          <DialogHeader className="border-b border-border px-6 py-4">
            <DialogTitle>White-label branding — {brandTarget?.name}</DialogTitle>
          </DialogHeader>
          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4" data-testid="partner-branding-scroll">
            <PartnerBrandingForm value={brandForm} onChange={setBrandForm}
                                 partnerName={brandTarget?.name} />
          </div>
          <DialogFooter className="border-t border-border px-6 py-4">
            <Button variant="outline" onClick={() => setBrandTarget(null)}>Cancel</Button>
            <Button data-testid="save-branding-button" onClick={saveBranding} className="bg-primary hover:bg-primary/90 text-primary-foreground">Save branding</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter 111 — Users panel rendered INLINE (no more modal) for
          both staff and partner viewers. `usersTarget` selects which
          org's users to render; staff can clear it via the back
          button, partners have it auto-set to their own org. */}
      {usersTarget && (
      <div className="mt-6 rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="partner-users-panel">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
          <h2 className="flex items-center gap-2 font-heading text-base font-semibold text-slate-800">
            <UsersIcon className="h-5 w-5 text-primary" /> Users — {usersTarget?.name}
          </h2>
          {!isPartnerViewer ? (
            <div className="flex items-center gap-4">
              <LastNameSwitch checked={userSort.byLast} onChange={userSort.setByLast} testid="partner-users-lastname-switch" />
              <UsersCsvButton rows={userSort.sorted} columns={PARTNER_USER_CSV} name={usersTarget?.name} testid="partner-users-csv-download" />
              <Button size="sm" variant="ghost" onClick={() => setUsersTarget(null)} data-testid="partner-users-back">
                <ArrowLeft className="h-4 w-4" /> Back to partners
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-4">
              <LastNameSwitch checked={userSort.byLast} onChange={userSort.setByLast} testid="partner-users-lastname-switch" />
              <UsersCsvButton rows={userSort.sorted} columns={PARTNER_USER_CSV} name={usersTarget?.name} testid="partner-users-csv-download" />
            </div>
          )}
        </div>
        <div className="space-y-3 p-5">
            {has("users.edit") && !loadingUsers && unwelcomedCount > 0 && (
              <div className="flex items-center justify-between rounded-lg border border-emerald-200 bg-emerald-50/70 px-3 py-2 text-xs" data-testid="partner-bulk-welcome-bar">
                <span className="text-emerald-800">
                  <b>{unwelcomedCount}</b> user{unwelcomedCount === 1 ? "" : "s"} in this partner {unwelcomedCount === 1 ? "hasn" : "haven"}&apos;t received a welcome yet.
                </span>
                <Button size="sm" variant="outline" data-testid="partner-bulk-welcome-btn"
                  onClick={bulkWelcome} disabled={bulkWelcoming}
                  className="h-7 border-emerald-300 bg-white text-emerald-700 hover:bg-emerald-100">
                  {bulkWelcoming ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />}
                  Welcome all {unwelcomedCount}
                </Button>
              </div>
            )}
            {loadingUsers ? (
              <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-slate-300" /></div>
            ) : (
              <div className="divide-y divide-slate-100 rounded-lg border border-slate-200" data-testid="partner-users-list">
                {partnerUsers.length > 0 && (
                  <div className="hidden items-center gap-2 bg-slate-50/80 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400 sm:flex">
                    <div className="min-w-0 flex-1">User · <NameSortHint sort={userSort.sort} byLast={userSort.byLast} testid="partner-users-name-order" /></div>
                    <div className="w-20 shrink-0 text-right"><SortHeader label="Last sign-in" sortKey="last_login" sort={userSort.sort} onToggle={userSort.toggle} testid="partner-users-sort-last-login" /></div>
                    {has("users.edit") && <div className="w-24 shrink-0 text-center">Invite</div>}
                    <div className="w-28 shrink-0 text-center"><SortHeader label="Role" sortKey="role" sort={userSort.sort} onToggle={userSort.toggle} testid="partner-users-sort-role" /></div>
                    <div className="w-20 shrink-0 text-center"><SortHeader label="Status" sortKey="status" sort={userSort.sort} onToggle={userSort.toggle} testid="partner-users-sort-status" /></div>
                    <div className="w-8 shrink-0" />
                  </div>
                )}
                {partnerUsers.length === 0 ? (
                  <div className="py-6 text-center text-sm text-slate-400">No users yet.</div>
                ) : userSort.sorted.map((u) => {
                  const lastLogin = u.last_login ? new Date(u.last_login) : null;
                  const lastLoginLabel = lastLogin
                    ? (() => {
                        const days = Math.floor((Date.now() - lastLogin.getTime()) / (1000 * 60 * 60 * 24));
                        if (days === 0) return "Today";
                        if (days === 1) return "Yesterday";
                        if (days < 7) return `${days}d ago`;
                        if (days < 30) return `${Math.floor(days / 7)}w ago`;
                        if (days < 365) return `${Math.floor(days / 30)}mo ago`;
                        return `${Math.floor(days / 365)}y ago`;
                      })()
                    : "Never";
                  return (
                    <div key={u.user_id} className="flex items-center gap-2 px-3 py-2 transition-colors hover:bg-slate-50/70" data-testid={`partner-user-${u.user_id}`}>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium text-slate-800">{u.name}</div>
                        <div className="truncate text-xs text-slate-400">{u.email}</div>
                      </div>
                      <span className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold ${u.mfa_enabled ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}
                            data-testid={`partner-user-mfa-${u.user_id}`}>{u.mfa_enabled ? "2FA on" : "2FA off"}</span>
                      <div
                        className={`w-20 shrink-0 text-right text-xs font-medium tabular-nums ${lastLogin ? "text-slate-600" : "text-slate-400"}`}
                        title={lastLogin ? `Last sign-in: ${lastLogin.toLocaleString()}` : "Never signed in"}
                        data-testid={`partner-user-lastlogin-${u.user_id}`}
                      >
                        {lastLoginLabel}
                      </div>
                      {has("users.edit") && (
                        <button data-testid={`partner-user-welcome-${u.user_id}`}
                          onClick={() => sendWelcome(u)} disabled={welcomingUserId === u.user_id}
                          title={u.welcome_sent_at ? `Resend welcome (last sent ${new Date(u.welcome_sent_at).toLocaleString()})` : "Send welcome email"}
                          className="inline-flex w-24 shrink-0 items-center justify-center gap-1 rounded-md border border-emerald-300 bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-700 transition-colors hover:bg-emerald-100 disabled:opacity-60">
                          {welcomingUserId === u.user_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />}
                          {u.welcome_sent_at ? "Resend" : "Welcome"}
                        </button>
                      )}
                      <span title={u.role_label || u.role}
                        className="hidden w-28 shrink-0 truncate rounded bg-slate-100 px-1.5 py-0.5 text-center text-[10px] font-medium text-slate-500 sm:block">
                        {u.role_label || u.role}
                      </span>
                      <div className="flex w-20 shrink-0 justify-center"><StatusChip status={u.status} /></div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`partner-user-actions-${u.user_id}`} className="h-8 w-8 shrink-0"><MoreHorizontal className="h-4 w-4" /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {has("users.edit") && <DropdownMenuItem data-testid={`partner-user-edit-${u.user_id}`} onClick={() => openEditUser(u)}><Pencil className="h-4 w-4" /> Edit user</DropdownMenuItem>}
                          <DropdownMenuItem data-testid={`partner-user-perms-${u.user_id}`} onClick={() => setPermsTarget(u)}><ShieldCheck className="h-4 w-4" /> View permissions</DropdownMenuItem>
                          {has("apps.edit") && <DropdownMenuItem data-testid={`partner-user-apps-${u.user_id}`} onClick={() => setAppsUserTarget({ id: u.user_id, name: u.name })}><AppWindow className="h-4 w-4" /> Manage apps</DropdownMenuItem>}
                          {canMasquerade && <DropdownMenuItem data-testid={`partner-user-masquerade-${u.user_id}`} onClick={() => startMasquerade(u)} className="text-amber-700 focus:text-amber-800"><VenetianMask className="h-4 w-4" /> Masquerade as {u.name.split(" ")[0]}</DropdownMenuItem>}
                          {has("users.edit") && u.status !== "active" && (
                            <DropdownMenuItem data-testid={`partner-user-activate-${u.user_id}`}
                              onClick={async () => {
                                try { await api.post(`/users/${u.user_id}/status`, { status: "active" }); toast.success("User activated"); reloadUsers(); }
                                catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Activate failed"); }
                              }}
                              className="text-emerald-700 focus:text-emerald-800">
                              <UserCheck className="h-4 w-4" /> Activate
                            </DropdownMenuItem>
                          )}
                          {has("users.edit") && (
                            <DropdownMenuItem data-testid={`partner-user-signout-${u.user_id}`} onClick={() => setSignOutTarget(u)}><LogOut className="h-4 w-4" /> Sign out everywhere</DropdownMenuItem>
                          )}
                          {canResetMfa(user, u) && (
                            <DropdownMenuItem data-testid={`partner-user-reset-mfa-${u.user_id}`} onClick={() => setMfaTarget(u)} className="text-rose-700 focus:text-rose-800"><KeyRound className="h-4 w-4" /> Reset 2FA</DropdownMenuItem>
                          )}
                          {has("users.edit") && u.status === "active" && (
                            <DropdownMenuItem data-testid={`partner-user-deactivate-${u.user_id}`}
                              onClick={async () => {
                                try { await api.post(`/users/${u.user_id}/status`, { status: "deactivated" }); toast.success("User deactivated"); reloadUsers(); }
                                catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Deactivate failed"); }
                              }}
                              className="text-amber-700 focus:text-amber-800">
                              <UserX className="h-4 w-4" /> Deactivate
                            </DropdownMenuItem>
                          )}
                          {has("users.delete") && (
                            <DropdownMenuItem data-testid={`partner-user-delete-${u.user_id}`}
                              onClick={async () => {
                                if (!window.confirm(`Delete ${u.name}? This cannot be undone.`)) return;
                                try { await api.delete(`/users/${u.user_id}`); toast.success("User deleted"); reloadUsers(); }
                                catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed"); }
                              }}
                              className="text-rose-700 focus:text-rose-800">
                              <Trash2 className="h-4 w-4" /> Delete
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  );
                })}
              </div>
            )}

            {has("users.create") && (addingUser ? (
              <AddPartnerUserForm
                usersTarget={usersTarget}
                partnerRoles={partnerRoles}
                onCancel={() => setAddingUser(false)}
                onCreated={onUserCreated}
              />
            ) : (
              <Button variant="outline" data-testid="partner-add-user-button" onClick={() => setAddingUser(true)} className="w-full"><Plus className="h-4 w-4" /> Add User</Button>
            ))}
          </div>
        </div>
      )}

      <EffectivePermsDialog userId={permsTarget?.user_id} userName={permsTarget?.name} open={!!permsTarget} onClose={() => setPermsTarget(null)} />
      <SignOutEverywhereDialog user={signOutTarget} onClose={() => setSignOutTarget(null)} />
      <ResetMfaDialog user={mfaTarget} onClose={() => setMfaTarget(null)} onDone={reloadUsers} />
      <AppAssignDialog scope="org" target={appsOrgTarget} open={!!appsOrgTarget} onClose={() => { setAppsOrgTarget(null); loadAppCounts(); }} />
      <AppAssignDialog scope="user" target={appsUserTarget} open={!!appsUserTarget} onClose={() => setAppsUserTarget(null)} />

      {/* Iter 110i — Edit partner user modal. Focused fields (name, phone,
          role, territory) — permissions/apps stay in their existing
          dedicated dialogs to keep this one uncluttered. Territory input
          shows only for partner-base_role and is required. */}
      <Dialog open={!!editingUser} onOpenChange={(o) => !o && closeEditUser()}>
        <DialogContent className="max-w-md" data-testid="partner-user-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit partner user</DialogTitle>
            <DialogDescription>{editingUser?.email}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-600">Name</label>
              <Input data-testid="partner-user-edit-name" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-600">Phone (optional)</label>
              <Input data-testid="partner-user-edit-phone" value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-600">Role</label>
              {canChangeRole ? (
                <Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v })}>
                  <SelectTrigger data-testid="partner-user-edit-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {partnerRoles.map((r) => <SelectItem key={r.role_id} value={r.role_id}>{r.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <p className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-medium text-slate-700"
                   data-testid="partner-user-edit-role-readonly">
                  {(partnerRoles.find((r) => r.role_id === editForm.role) || {}).name || editForm.role || "—"}
                </p>
              )}
            </div>
            {canSetQuoter && (
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-slate-600">
                  Quoter username <span className="font-normal text-slate-400">(defaults to the user&apos;s email)</span>
                </label>
                <Input data-testid="partner-user-edit-quoter" placeholder={editingUser?.email || "e.g. jsmith2005"}
                  value={editForm.quoter_username}
                  onChange={(e) => setEditForm({ ...editForm, quoter_username: e.target.value })} />
                <p className="text-[10px] text-slate-400">Used to sign this user into Quotes. Leave blank to use their login email.</p>
              </div>
            )}
            {(() => {
              const roleMeta = partnerRoles.find((r) => r.role_id === editForm.role);
              const isPartnerRole = (roleMeta?.base_role || editForm.role) === "partner";
              const parentTerr = (usersTarget?.territory || "").trim();
              if (!isPartnerRole) return null;
              return (
                <div className="rounded border border-slate-100 bg-slate-50 px-3 py-2 text-xs" data-testid="partner-user-edit-territory-inherit">
                  <div className="font-medium text-slate-600">Territory (inherited from partner)</div>
                  {parentTerr ? (
                    <div className="mt-0.5 font-mono text-slate-800">{parentTerr}</div>
                  ) : (
                    <div className="mt-0.5 text-amber-700">⚠ Not set — edit the partner org to fill it in. All users inherit.</div>
                  )}
                  <p className="mt-1 text-[10px] text-slate-400">
                    Managed on the Partner record so a single edit retargets every user in this org.
                  </p>
                </div>
              );
            })()}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeEditUser}>Cancel</Button>
            <Button data-testid="partner-user-edit-save" onClick={saveEditUser} disabled={savingEdit} className="gap-1.5">
              {savingEdit && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {preview.dialog}

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete partner?</AlertDialogTitle>
            <AlertDialogDescription>This removes {deleteTarget?.name}. This action cannot be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-partner" onClick={doDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Iter 88d: Partner Merge dialog — folds one partner into another
          (moves every DID, merges aliases, adds source name as an alias
          on target). Super-admin only. Preview loads server-side counts
          the moment the user picks a target so they see exactly what
          will move before confirming. */}
      <Dialog open={!!mergeSource} onOpenChange={(o) => { if (!o) setMergeSource(null); }}>
        <DialogContent className="max-w-lg" data-testid="partner-merge-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GitMerge className="h-4 w-4 text-amber-600" />
              Merge <span className="font-mono">{mergeSource?.name}</span> into…
            </DialogTitle>
            <DialogDescription>
              Every DID currently on <b>{mergeSource?.name}</b> will move to
              the target partner. The source&rsquo;s name and aliases will be
              added to the target so future CSV imports keep landing on the
              right partner. This action is auditable but can&rsquo;t be
              undone via one click.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3">
            <div className="grid gap-1.5">
              <Label>Target partner</Label>
              <Select value={mergeTargetId} onValueChange={loadMergePreview}>
                <SelectTrigger data-testid="merge-target-select">
                  <SelectValue placeholder="— pick a partner —" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {orgs.filter((o) => o.org_id !== mergeSource?.org_id).map((o) => (
                    <SelectItem key={o.org_id} value={o.org_id}>{o.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {mergePreview && (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm"
                   data-testid="merge-preview">
                <div className="mb-1.5 grid grid-cols-2 gap-2">
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Source</div>
                    <div className="font-medium">{mergePreview.source.name}</div>
                    <div className="text-[11px] text-slate-500">{mergePreview.source.aliases.length} alias{mergePreview.source.aliases.length === 1 ? "" : "es"}</div>
                  </div>
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Target</div>
                    <div className="font-medium">{mergePreview.target.name}</div>
                    <div className="text-[11px] text-slate-500">{mergePreview.target.aliases.length} alias{mergePreview.target.aliases.length === 1 ? "" : "es"}</div>
                  </div>
                </div>
                <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                  <div className="rounded bg-white px-2 py-1.5">
                    <div className="text-[10px] uppercase text-slate-500">DIDs to move</div>
                    <div className="text-lg font-bold text-primary" data-testid="merge-dids-count">{mergePreview.dids_to_move.toLocaleString()}</div>
                  </div>
                  <div className="rounded bg-white px-2 py-1.5">
                    <div className="text-[10px] uppercase text-slate-500">New aliases on target</div>
                    <div className="text-lg font-bold text-slate-700">{mergePreview.new_aliases_on_target.length}</div>
                  </div>
                </div>
                {mergePreview.users_on_source > 0 && (
                  <div className="mt-2 rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-800"
                       data-testid="merge-users-warning">
                    <b>{mergePreview.users_on_source}</b> user{mergePreview.users_on_source === 1 ? "" : "s"} still belong to the source. Users are <em>not</em> moved automatically — reassign them manually first if needed.
                  </div>
                )}
                {mergePreview.new_aliases_on_target.length > 0 && (
                  <div className="mt-2 text-[10px] text-slate-500">
                    Adds aliases: <span className="font-mono">{mergePreview.new_aliases_on_target.join(", ")}</span>
                  </div>
                )}
              </div>
            )}
            <label className="flex items-center gap-2 text-xs text-slate-600">
              <input type="checkbox" checked={mergeDelete}
                     onChange={(e) => setMergeDelete(e.target.checked)}
                     data-testid="merge-delete-source" />
              Also delete the source partner record after the merge (default: soft-mark as merged).
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" disabled={mergeBusy} onClick={() => setMergeSource(null)}
                    data-testid="merge-cancel">Cancel</Button>
            <Button onClick={runMerge}
                    disabled={mergeBusy || !mergeTargetId || !mergePreview}
                    data-testid="merge-confirm"
                    className="gap-2 bg-amber-600 hover:bg-amber-700 text-white">
              {mergeBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <GitMerge className="h-4 w-4" />}
              Merge {mergePreview?.dids_to_move ? `${mergePreview.dids_to_move.toLocaleString()} DIDs` : "partner"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// --------------------------------------------------------------------------
// AddPartnerUserForm — Iter 110ai. Extracted from <Partners> to keep the
// giant parent tree out of the render loop on every keystroke. Owns its
// own form + submitting state so typing in the inputs does NOT force
// Partners to re-render the entire table/widgets.
// --------------------------------------------------------------------------
const AddPartnerUserForm = React.memo(function AddPartnerUserForm({ usersTarget, partnerRoles, onCancel, onCreated }) {
  const { user, has } = useAuth();
  const canSetQuoter = user?.role === "super_admin" || has("quotes.edit");
  const canChangeRole = user?.role === "super_admin" || has("user_roles.edit");
  const [form, setForm] = useState(EMPTY_USER);
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    setSaving(true);
    try {
      const body = { ...form, organization_id: usersTarget.org_id };
      if (!body.quoter_username?.trim()) delete body.quoter_username;
      await api.post("/users", body);
      toast.success("User added");
      setForm(EMPTY_USER);
      await onCreated();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };
  const roleMeta = partnerRoles.find((r) => r.role_id === form.role);
  const isPartnerRole = (roleMeta?.base_role || form.role) === "partner";
  const parentTerr = (usersTarget?.territory || "").trim();
  return (
    <div className="space-y-2 rounded-lg border border-slate-200 p-3" data-testid="partner-add-user-form">
      <div className="grid grid-cols-2 gap-2">
        <Input data-testid="partner-user-name" placeholder="Full name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
        <Input data-testid="partner-user-email" type="email" placeholder="Email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Input data-testid="partner-user-password" type="password" placeholder="Temp password" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} />
        {canChangeRole ? (
          <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
            <SelectTrigger data-testid="partner-user-role"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="partner">Partner</SelectItem>
              {partnerRoles.filter((r) => r.role_id !== "partner").map((r) => <SelectItem key={r.role_id} value={r.role_id}>{r.name}</SelectItem>)}
            </SelectContent>
          </Select>
        ) : (
          <p className="flex items-center rounded-md border border-slate-200 bg-slate-50 px-3 text-sm font-medium text-slate-700"
             data-testid="partner-user-role-readonly">
            {(partnerRoles.find((r) => r.role_id === form.role) || {}).name || "Partner"}
          </p>
        )}
      </div>
      {canSetQuoter && (
        <Input data-testid="partner-user-quoter" placeholder="Quoter username (optional — defaults to email)"
          value={form.quoter_username} onChange={(e) => setForm((f) => ({ ...f, quoter_username: e.target.value }))} />
      )}
      {isPartnerRole && (
        <div className="rounded border border-slate-100 bg-slate-50 px-2.5 py-1.5 text-[11px]" data-testid="partner-user-territory-inherit-note">
          {parentTerr ? (
            <>Territory <span className="font-semibold text-slate-700">{parentTerr}</span> is inherited from this partner.</>
          ) : (
            <span className="text-amber-700">⚠ This partner has no Territory set. Edit the partner org and fill it in — every user will inherit the value automatically.</span>
          )}
        </div>
      )}
      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onCancel}>Cancel</Button>
        <Button size="sm" data-testid="partner-user-save" onClick={submit} disabled={saving} className="bg-primary hover:bg-primary/90 text-primary-foreground">
          {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Add user
        </Button>
      </div>
    </div>
  );
});

